import * as THREE from 'three';

/**
 * TurntableControls - Orbit around object, zoom, pan
 * For viewing objects from all angles
 */
export class TurntableControls {
  constructor(camera, domElement) {
    this.camera = camera;
    this.domElement = domElement;
    
    // Orbit state
    this.target = new THREE.Vector3(0, 0, 0);
    this.spherical = new THREE.Spherical();
    
    // Settings
    this.rotateSpeed = 0.005;
    this.zoomSpeed = 0.1;
    this.panSpeed = 0.01;
    this.minDistance = 0.5;
    this.maxDistance = 50;
    this.minPolarAngle = 0.1;
    this.maxPolarAngle = Math.PI - 0.1;
    this.enablePan = true;
    this.autoRotate = false;
    this.autoRotateSpeed = 0.5;
    
    // Interaction state
    this.isDragging = false;
    this.isPanning = false;
    this.isPinching = false;
    this.wasActualDrag = false;
    this.dragStart = new THREE.Vector2();
    this.dragDelta = new THREE.Vector2();
    this.totalDragDistance = 0;
    this.dragThreshold = 5;
    this.lastTouchDistance = 0;
    
    // Initialize
    this.updateSphericalFromCamera();
    this.setupEventListeners();
  }

  updateSphericalFromCamera() {
    const offset = new THREE.Vector3();
    offset.copy(this.camera.position).sub(this.target);
    this.spherical.setFromVector3(offset);
  }

  setupEventListeners() {
    this.domElement.addEventListener('mousedown', (e) => this.onMouseDown(e));
    this.domElement.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.domElement.addEventListener('mouseup', (e) => this.onMouseUp(e));
    this.domElement.addEventListener('mouseleave', (e) => this.onMouseUp(e));
    this.domElement.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    this.domElement.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false });
    this.domElement.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
    this.domElement.addEventListener('touchend', (e) => this.onTouchEnd(e));
    this.domElement.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  onMouseDown(event) {
    if (event.button === 0) {
      // Left click - rotate
      this.isDragging = true;
      this.isPanning = false;
    } else if (event.button === 2) {
      // Right click - pan
      this.isDragging = true;
      this.isPanning = true;
    }
    this.wasActualDrag = false;
    this.totalDragDistance = 0;
    this.dragStart.set(event.clientX, event.clientY);
    this.domElement.style.cursor = 'grabbing';
  }

  onMouseMove(event) {
    if (!this.isDragging) return;
    
    this.dragDelta.set(
      event.clientX - this.dragStart.x,
      event.clientY - this.dragStart.y
    );
    
    this.totalDragDistance += Math.abs(this.dragDelta.x) + Math.abs(this.dragDelta.y);
    
    if (this.totalDragDistance > this.dragThreshold) {
      this.wasActualDrag = true;
      if (this.isPanning && this.enablePan) {
        this.pan(this.dragDelta.x, this.dragDelta.y);
      } else {
        this.rotate(this.dragDelta.x, this.dragDelta.y);
      }
    }
    
    this.dragStart.set(event.clientX, event.clientY);
  }

  onMouseUp(event) {
    this.isDragging = false;
    this.isPanning = false;
    this.domElement.style.cursor = 'grab';
    setTimeout(() => { this.wasActualDrag = false; }, 50);
  }

  onWheel(event) {
    event.preventDefault();
    const delta = event.deltaY > 0 ? 1 : -1;
    this.zoom(delta);
  }

  onTouchStart(event) {
    event.preventDefault();
    this.wasActualDrag = false;
    this.totalDragDistance = 0;
    
    if (event.touches.length === 1) {
      this.isDragging = true;
      this.dragStart.set(event.touches[0].clientX, event.touches[0].clientY);
    } else if (event.touches.length === 2) {
      this.isPinching = true;
      this.isDragging = false;
      this.lastTouchDistance = this.getTouchDistance(event.touches);
    }
  }

  onTouchMove(event) {
    event.preventDefault();
    
    if (event.touches.length === 1 && this.isDragging) {
      this.dragDelta.set(
        event.touches[0].clientX - this.dragStart.x,
        event.touches[0].clientY - this.dragStart.y
      );
      this.totalDragDistance += Math.abs(this.dragDelta.x) + Math.abs(this.dragDelta.y);
      
      if (this.totalDragDistance > this.dragThreshold) {
        this.wasActualDrag = true;
        this.rotate(this.dragDelta.x, this.dragDelta.y);
      }
      this.dragStart.set(event.touches[0].clientX, event.touches[0].clientY);
    } else if (event.touches.length === 2 && this.isPinching) {
      const distance = this.getTouchDistance(event.touches);
      const delta = this.lastTouchDistance - distance;
      this.zoom(delta * 0.02);
      this.lastTouchDistance = distance;
    }
  }

  onTouchEnd(event) {
    if (event.touches.length === 0) {
      this.isDragging = false;
      this.isPinching = false;
    } else if (event.touches.length === 1) {
      this.isPinching = false;
      this.isDragging = true;
      this.dragStart.set(event.touches[0].clientX, event.touches[0].clientY);
    }
    setTimeout(() => { this.wasActualDrag = false; }, 50);
  }

  getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  rotate(deltaX, deltaY) {
    this.spherical.theta -= deltaX * this.rotateSpeed;
    this.spherical.phi += deltaY * this.rotateSpeed;
    this.spherical.phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this.spherical.phi));
    this.updateCameraPosition();
  }

  zoom(delta) {
    this.spherical.radius += delta * this.zoomSpeed * this.spherical.radius;
    this.spherical.radius = Math.max(this.minDistance, Math.min(this.maxDistance, this.spherical.radius));
    this.updateCameraPosition();
  }

  pan(deltaX, deltaY) {
    const offset = new THREE.Vector3();
    const right = new THREE.Vector3();
    const up = new THREE.Vector3();
    
    this.camera.getWorldDirection(offset);
    right.crossVectors(offset, this.camera.up).normalize();
    up.crossVectors(right, offset).normalize();
    
    const panX = -deltaX * this.panSpeed * this.spherical.radius;
    const panY = deltaY * this.panSpeed * this.spherical.radius;
    
    this.target.addScaledVector(right, panX);
    this.target.addScaledVector(up, panY);
    
    this.updateCameraPosition();
  }

  updateCameraPosition() {
    const offset = new THREE.Vector3();
    offset.setFromSpherical(this.spherical);
    this.camera.position.copy(this.target).add(offset);
    this.camera.lookAt(this.target);
  }

  update(delta) {
    if (this.autoRotate && !this.isDragging) {
      this.spherical.theta -= this.autoRotateSpeed * delta;
      this.updateCameraPosition();
    }
  }

  dispose() {
    // Event listeners are bound to domElement, will be cleaned up when element is removed
  }
}
